import { useEffect, useState } from "react";

import { api, ApiError } from "../lib/api";
import { useAuth } from "../context/auth";
import { useLive } from "../lib/use-live";
import { useT } from "../context/locale";
import { useToast } from "../context/toast";
import { useDuration } from "../lib/use-relative";
import { countdown, dateTime, odds, relative, timeOnly } from "../lib/format";
import {
  ActionButton,
  ActionIcon,
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  LoadingRows,
  Modal,
  NoRowActions,
  NumberChip,
  Panel,
  RowActions,
  Table,
  Td,
  Tr,
} from "../components/ui";
import {
  FieldGrid,
  NumberField,
  NumberSlots,
  SelectField,
  TextAreaField,
  TextField,
  Toggle,
} from "../components/ui/form";
import type { TranslationKey } from "../i18n";
import { GAME_INTERACTIONS } from "../types/api";
import type {
  AdminGame,
  GameInteraction,
  UpcomingRound,
  UpcomingRounds,
} from "../types/api";

/**
 * Games.
 *
 * Round settings used to live in the server environment, which meant changing
 * how many numbers a round draws was a redeploy. They are stored per game now.
 *
 * So is the hall itself. The tiles were a fixed list of six in the server's
 * bootstrap code, which made "add a game" and "rename a game" both a deploy;
 * they are rows an operator owns from here — add, edit, hide, delete.
 *
 * Hiding and deleting answer different questions and the screen keeps them
 * apart. Hiding withholds a tile from the player hall and is reversible from
 * this same list. Deleting is permanent, and the API refuses it outright for a
 * game with rounds behind it, because those rounds carry bets and ledger rows
 * that point at the row being removed.
 */
export function Games() {
  const t = useT();
  const { allowed } = useAuth();
  const games = useLive<{ items: AdminGame[] }>("games", {});
  const [editing, setEditing] = useState<AdminGame | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<AdminGame | null>(null);
  const [voiding, setVoiding] = useState<AdminGame | null>(null);

  /*
   * The playable game gets its own card above the hall, because what an
   * operator does with it — start it, pause it, restart the round — has nothing
   * in common with arranging tiles.
   *
   * Found by its flag rather than by the `lotto` slug: the hall is editable, so
   * which game is the playable one is data now.
   */
  const live = games.data?.items.find((game) => game.isPlayable) ?? null;
  const others = games.data?.items.filter((game) => game !== live) ?? [];

  const canCreate = allowed("games", "create");
  const canUpdate = allowed("games", "update");
  const canDelete = allowed("games", "delete");

  return (
    <div className="space-y-5">
      {games.error ? (
        <ErrorNote message={games.error} onRetry={games.reload} />
      ) : null}

      {live ? (
        <LiveGameCard
          game={live}
          canUpdate={canUpdate}
          onEdit={() => setEditing(live)}
          onVoid={() => setVoiding(live)}
          onChanged={games.reload}
        />
      ) : null}

      {/* Only for the playable game: a display-only tile never draws a round,
          so a queue of results for it would be a list of numbers that can
          never happen. */}
      {live ? <UpcomingRoundsPanel game={live} canUpdate={canUpdate} /> : null}

      <Panel
        eyebrow={t("games.gameHall")}
        title={t("games.otherTiles")}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={games.reload}>
              {t("action.refresh")}
            </Button>
            {canCreate ? (
              <Button size="sm" variant="gold" onClick={() => setCreating(true)}>
                {t("games.addTile")}
              </Button>
            ) : null}
          </div>
        }
      >
        <p className="text-muted mb-4 text-[13px]">{t("games.tilesNote")}</p>

        {others.length === 0 ? (
          <EmptyState
            title={t("games.noTiles")}
            detail={canCreate ? t("games.noTilesDetail") : undefined}
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {others.map((game) => (
              <TileCard
                key={game.id}
                game={game}
                canUpdate={canUpdate}
                canDelete={canDelete}
                onEdit={() => setEditing(game)}
                onDelete={() => setDeleting(game)}
                onChanged={games.reload}
              />
            ))}
          </div>
        )}
      </Panel>

      <GameDialog
        game={editing}
        open={creating || Boolean(editing)}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        onDone={games.reload}
      />
      <DeleteTileDialog
        game={deleting}
        onClose={() => setDeleting(null)}
        onDone={games.reload}
      />
      <VoidRoundDialog
        game={voiding}
        onClose={() => setVoiding(null)}
        onDone={games.reload}
      />
    </div>
  );
}

/**
 * One hall tile.
 *
 * A hidden tile is dimmed rather than moved to a separate list: it is still
 * part of the hall's running order, and an operator deciding what to unhide
 * wants to see it where it will reappear.
 */
function TileCard({
  game,
  canUpdate,
  canDelete,
  onEdit,
  onDelete,
  onChanged,
}: {
  game: AdminGame;
  canUpdate: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onChanged: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  /** The one-click half of the edit form, because it is the switch most used. */
  async function toggleHidden() {
    setBusy(true);
    try {
      const hide = !game.isHidden;
      await api.updateGame(game.id, {
        isHidden: hide,
        reason: hide ? t("games.hiddenFromConsole") : t("games.shownFromConsole"),
      });
      toast.success(
        hide ? t("toast.tileHidden") : t("toast.tileShown"),
        hide ? t("toast.tileHiddenDetail") : t("toast.tileShownDetail"),
      );
      onChanged();
    } catch (caught) {
      toast.error(
        t("error.changeGame"),
        caught instanceof ApiError ? caught.message : undefined,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="rounded-lg border p-3"
      style={{
        borderColor: "var(--border)",
        // Dimmed, not hidden — see the note on this component.
        opacity: game.isHidden ? 0.55 : 1,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold">{game.name}</p>
          <p className="text-dim truncate text-[11px]">{game.subtitle}</p>
          <p className="money text-dim mt-1 truncate text-[11px]">{game.slug}</p>
        </div>
        {game.badge ? <Badge tone="neutral">{game.badge}</Badge> : null}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Badge tone={game.isPlayable ? "success" : "neutral"}>
          {t(`games.interaction.${game.interaction}` as TranslationKey)}
        </Badge>
        <Badge tone={game.isHidden ? "warning" : "neutral"}>
          {game.isHidden ? t("games.hiddenBadge") : t("games.inHallBadge")}
        </Badge>
      </div>

      {canUpdate || canDelete ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {canUpdate ? (
            <>
              <Button size="sm" variant="ghost" onClick={onEdit}>
                {t("action.edit")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={busy}
                onClick={toggleHidden}
              >
                {game.isHidden ? t("games.show") : t("games.hide")}
              </Button>
            </>
          ) : null}
          {canDelete ? (
            <Button size="sm" variant="ghost" onClick={onDelete}>
              {t("action.delete")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Removes a tile from the hall.
 *
 * The API refuses this for a game with rounds behind it, and the dialog says so
 * before the operator clicks rather than letting them find out from an error —
 * with the alternative, hiding, named in the same breath.
 */
function DeleteTileDialog({
  game,
  onClose,
  onDone,
}: {
  game: AdminGame | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close() {
    setReason("");
    setError(null);
    onClose();
  }

  const rounds = game?.roundCount ?? 0;
  const blocked = rounds > 0 || Boolean(game?.isRunning);

  async function submit() {
    if (!game) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.deleteGame(game.id, reason.trim() || undefined);
      toast.success(t("toast.tileDeleted"), t("toast.tileDeletedDetail", { name: result.name }));
      onDone();
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : t("error.deleteGame"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(game)}
      onClose={close}
      eyebrow={t("nav.games")}
      title={t("games.deleteTitle", { name: game?.name ?? "" })}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={submit}
            loading={busy}
            disabled={blocked}
          >
            {t("games.deleteSubmit")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {blocked ? (
          <p
            className="rounded-lg px-3 py-2 text-[12px]"
            style={{ background: "var(--warning-bg)", color: "var(--warning)" }}
          >
            {game?.isRunning
              ? t("games.deleteRunning")
              : t("games.deleteHasRounds", { count: rounds })}
          </p>
        ) : null}

        <p className="text-muted text-[13px]">{t("games.deleteNote")}</p>

        <TextAreaField
          label={t("common.reason")}
          value={reason}
          onChange={setReason}
          placeholder={t("games.deleteReasonPlaceholder")}
          hint={t("common.reasonHint")}
        />

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

/**
 * Ends the round that is counting down and starts a fresh one.
 *
 * Confirmation rather than a bare button because it refunds live money: every
 * stake on the round comes back, and the dialog says how many before you commit.
 */
function VoidRoundDialog({
  game,
  onClose,
  onDone,
}: {
  game: AdminGame | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const live = game?.liveRound ?? null;

  function close() {
    setReason("");
    setError(null);
    onClose();
  }

  async function submit() {
    if (!game || !reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.voidRound(game.id, reason.trim());
      toast.success(
        t("toast.roundVoidedRestarted"),
        t("toast.roundVoidedDetail", {
          count: result.refunded,
          period: t("common.roundNo", { drawNo: result.next.drawNo }),
        }),
      );
      onDone();
      close();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("error.voidRound"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(game)}
      onClose={close}
      eyebrow={t("section.games")}
      title={t("games.voidTitle")}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={submit}
            loading={busy}
            disabled={!reason.trim() || !live}
          >
            {t("games.voidSubmit")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {live ? (
          <>
            <div
              className="rounded-lg px-3 py-2 text-[12px]"
              style={{ background: "var(--warning-bg)", color: "var(--warning)" }}
            >
              {t("games.voidBlurb", {
                period: t("common.roundNo", { drawNo: live.drawNo }),
              })}
            </div>
            <p className="text-muted text-[12px]">{t("games.voidWhy")}</p>
            <p className="text-dim text-[12px]">
              <Badge tone="neutral">
                {t("games.voidBets", { count: live.betCount ?? 0 })}
              </Badge>
            </p>
          </>
        ) : (
          <p className="text-muted text-[13px]">{t("games.voidNoRound")}</p>
        )}

        <TextAreaField
          label={t("common.reason")}
          value={reason}
          onChange={setReason}
          placeholder={t("games.voidReasonPlaceholder")}
          hint={t("common.reasonHint")}
          required
        />

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

/** The playable game, with its live state and the run switch. */
function LiveGameCard({
  game,
  canUpdate,
  onEdit,
  onVoid,
  onChanged,
}: {
  game: AdminGame;
  canUpdate: boolean;
  onEdit: () => void;
  onVoid: () => void;
  onChanged: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const duration = useDuration();
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState<number | null>(null);
  /**
   * The second countdown: how long the round will still take bets.
   *
   * Counted against the round's own `betsCloseAt` rather than subtracting the
   * game's current window from `closesAt` — the round keeps the window it
   * opened under, so an operator who has just changed the setting would
   * otherwise see a boundary the round is not actually using.
   */
  const [betSeconds, setBetSeconds] = useState<number | null>(null);

  useEffect(() => {
    if (!game.liveRound) {
      setSeconds(null);
      setBetSeconds(null);
      return;
    }
    const until = (iso: string) =>
      Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 1000));
    const tick = () => {
      setSeconds(until(game.liveRound!.closesAt));
      setBetSeconds(until(game.liveRound!.betsCloseAt));
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [game.liveRound]);

  const lockdown = game.liveRound?.lockdownSeconds ?? 0;
  const inLockdown = lockdown > 0 && betSeconds === 0;

  async function toggle() {
    setBusy(true);
    try {
      const next = !game.isRunning;
      await api.updateGame(game.id, {
        isRunning: next,
        reason: next ? t("games.startedFromConsole") : t("games.pausedFromConsole"),
      });
      toast.success(
        next ? t("toast.gameStarted") : t("dashboard.gamePaused"),
        next
          ? t("games.startedDetail")
          : t("games.pausedDetail"),
      );
      onChanged();
    } catch (caught) {
      toast.error(
        t("error.changeGame"),
        caught instanceof ApiError ? caught.message : undefined,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel overflow-hidden">
      <div
        className="flex flex-wrap items-center justify-between gap-4 border-b px-5 py-4"
        style={{
          borderColor: "var(--border)",
          background: game.isRunning
            ? "linear-gradient(90deg, var(--color-crimson), transparent 70%)"
            : "var(--surface-inset)",
        }}
      >
        <div>
          <p className="eyebrow">{t("games.playable")}</p>
          <h2 className="display mt-1 text-[22px]">{game.name}</h2>
        </div>
        <div className="flex items-center gap-3">
          <Badge tone={game.isRunning ? "success" : "danger"}>
            {game.isRunning ? "● running" : "■ paused"}
          </Badge>
          {/* A playable game withheld from the hall is worth saying out loud:
              it can still be running, and its absence is otherwise only visible
              by comparing this screen against the player app. */}
          {game.isHidden ? (
            <Badge tone="warning">{t("games.hiddenBadge")}</Badge>
          ) : null}
          {canUpdate ? (
            <>
              <Button
                variant={game.isRunning ? "danger" : "primary"}
                loading={busy}
                onClick={toggle}
              >
                {game.isRunning ? t("games.pause") : t("games.start")}
              </Button>
              {/* Voiding is the other half of editing the settings: those only
                  apply to rounds opened afterwards, so this is what lets them take
                  effect now rather than at the end of the countdown. */}
              <Button variant="danger" onClick={onVoid} disabled={!game.liveRound}>
                {t("games.voidRound")}
              </Button>
              <Button onClick={onEdit}>{t("nav.settings")}</Button>
            </>
          ) : null}
        </div>
      </div>

      <div className="grid gap-5 p-5 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <p className="eyebrow mb-2">{t("games.currentRound")}</p>
          {game.liveRound ? (
            <div className="flex flex-wrap items-end gap-6">
              <div>
                <p className="money text-[13px]">
                  {t("common.roundNo", { drawNo: game.liveRound.drawNo })}
                </p>
              </div>
              <div>
                <p
                  className="money text-[38px] leading-none"
                  style={{
                    color:
                      seconds !== null && seconds <= 30
                        ? "var(--danger)"
                        : "var(--accent)",
                  }}
                >
                  {seconds === null ? "--:--" : countdown(seconds)}
                </p>
                <p className="eyebrow mt-1">{t("games.untilCloses")}</p>
              </div>
              {/* Only shown when a window is configured — with none, the two
                  countdowns are the same number and the second one is noise. */}
              {lockdown > 0 ? (
                <div>
                  <p
                    className="money text-[26px] leading-none"
                    style={{ color: inLockdown ? "var(--danger)" : "var(--text)" }}
                  >
                    {betSeconds === null ? "--:--" : countdown(betSeconds)}
                  </p>
                  <p className="eyebrow mt-1">
                    {inLockdown ? t("games.inLockdown") : t("games.untilBetsClose")}
                  </p>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-dim text-[13px]">
              {game.isRunning
                ? t("games.opening")
                : t("games.noRound")}
            </p>
          )}

          {game.lastResult ? (
            <div className="mt-5">
              <p className="eyebrow mb-2">
                {t("games.lastResult")} ·{" "}
                {t("common.roundNo", { drawNo: game.lastResult.drawNo })} ·{" "}
                {relative(game.lastResult.settledAt)}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {game.lastResult.results.map((value, index) => (
                  <NumberChip key={index} value={value} />
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <dl className="grid gap-x-6 gap-y-3 self-start text-[13px] sm:grid-cols-2">
          <Setting
            label={t("games.numbersPerRound")}
            value={game.config.positionCount}
          />
          <Setting
            label={t("games.roundEvery")}
            value={duration(game.config.intervalSeconds)}
          />
          <Setting
            label={t("games.lockdown")}
            value={
              game.config.lockdownSeconds > 0
                ? duration(game.config.lockdownSeconds)
                : t("games.lockdownOff")
            }
          />
          <Setting
            label={t("games.numberRange")}
            value={`${game.config.numberMin}–${game.config.numberMax}`}
          />
          <Setting label={t("games.odds")} value={odds(game.config.oddsBasisPoints)} />
          <Setting
            label={t("games.roundsSoFar")}
            value={game.roundCount.toLocaleString()}
          />
          <Setting
            label={t("games.inHall")}
            value={
              game.isHidden
                ? t("games.hidden")
                : t(`games.interaction.${game.interaction}` as TranslationKey)
            }
          />
        </dl>
      </div>
    </section>
  );
}

function Setting({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="eyebrow">{label}</dt>
      <dd className="money mt-1 text-[15px]">{value}</dd>
    </div>
  );
}

/**
 * One form for adding a tile and for editing one.
 *
 * They are the same fields plus a slug, and splitting them into two dialogs
 * would mean two places to keep the validation honest. `game === null` with the
 * dialog open is the create case.
 *
 * Round settings appear only once a tile is marked playable, and only when
 * editing: they are meaningless on a display-only tile, and the create route
 * does not accept them — a new tile starts on the defaults and becomes a
 * running game through a second, deliberate save.
 */
function GameDialog({
  game,
  open,
  onClose,
  onDone,
}: {
  game: AdminGame | null;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const duration = useDuration();
  const creating = open && !game;

  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [badge, setBadge] = useState("");
  const [tileImage, setTileImage] = useState("");
  const [sortOrder, setSortOrder] = useState("0");
  const [positions, setPositions] = useState("6");
  const [interval, setIntervalValue] = useState("300");
  const [lockdown, setLockdown] = useState("0");
  const [numberMin, setNumberMin] = useState("1");
  const [numberMax, setNumberMax] = useState("10");
  const [oddsBp, setOddsBp] = useState("19800");
  const [interaction, setInteraction] = useState<GameInteraction>("disabled");
  const [isHidden, setIsHidden] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSlug("");
    setBadge(game?.badge ?? "");
    setTileImage(game?.tileImage ?? "");
    setName(game?.name ?? "");
    setSubtitle(game?.subtitle ?? "");
    setSortOrder(String(game?.sortOrder ?? 0));
    setPositions(String(game?.config.positionCount ?? 6));
    setIntervalValue(String(game?.config.intervalSeconds ?? 300));
    setLockdown(String(game?.config.lockdownSeconds ?? 0));
    setNumberMin(String(game?.config.numberMin ?? 1));
    setNumberMax(String(game?.config.numberMax ?? 10));
    setOddsBp(String(game?.config.oddsBasisPoints ?? 19800));
    setInteraction(game?.interaction ?? "disabled");
    setIsHidden(game?.isHidden ?? false);
    setReason("");
    setError(null);
  }, [game, open]);

  const positionsNum = Number(positions);
  const intervalNum = Number(interval);
  const lockdownNum = Number(lockdown);
  const minNum = Number(numberMin);
  const maxNum = Number(numberMax);
  const oddsNum = Number(oddsBp);
  const sortNum = Number(sortOrder);

  /**
   * Round settings are only edited — and only validated — where they apply.
   *
   * `clickable` is what makes a tile playable, so it is also what decides
   * whether there is a round to configure. The API derives the same thing from
   * the same value; there is no second switch on either side.
   */
  const opensTheGame = interaction === "clickable";
  const showRoundSettings = !creating && opensTheGame;

  const rangeInvalid = maxNum <= minNum;
  /*
   * The same rule the API enforces, checked against the interval in this form
   * rather than the saved one — an operator lengthening the window and the
   * round together should not be stopped halfway through typing.
   */
  const lockdownInvalid =
    !Number.isInteger(lockdownNum) || lockdownNum < 0 || lockdownNum >= intervalNum;
  /** Mirrors the API's slug rule, so a typo is caught before the round trip. */
  const slugInvalid = creating && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug.trim());
  const sortInvalid = !Number.isInteger(sortNum) || sortNum < 0 || sortNum > 9_999;

  const roundSettingsValid =
    !showRoundSettings ||
    (positionsNum >= 1 &&
      positionsNum <= 20 &&
      intervalNum >= 30 &&
      intervalNum <= 86_400 &&
      !rangeInvalid &&
      !lockdownInvalid &&
      oddsNum >= 10_000);

  const valid =
    name.trim().length > 0 && !slugInvalid && !sortInvalid && roundSettingsValid;

  async function submit() {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const tile = {
        name: name.trim(),
        subtitle: subtitle.trim(),
        badge: badge.trim() || null,
        tileImage: tileImage.trim() || null,
        interaction,
        isHidden,
        sortOrder: sortNum,
        reason: reason.trim() || undefined,
      };

      if (creating) {
        const created = await api.createGame({ ...tile, slug: slug.trim() });
        toast.success(t("toast.tileCreated"), t("toast.tileCreatedDetail", { name: created.name }));
      } else {
        await api.updateGame(game!.id, {
          ...tile,
          // Only sent where they mean something — see `showRoundSettings`.
          ...(showRoundSettings
            ? {
                config: {
                  positionCount: positionsNum,
                  intervalSeconds: intervalNum,
                  lockdownSeconds: lockdownNum,
                  numberMin: minNum,
                  numberMax: maxNum,
                  oddsBasisPoints: oddsNum,
                },
              }
            : {}),
        });
        toast.success(t("toast.gameUpdated"), t("toast.gameSettingsNote"));
      }
      onDone();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : t("error.saveGame"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow={t("nav.games")}
      title={
        creating
          ? t("games.addTitle")
          : t("games.settingsTitle", { name: game?.name ?? "" })
      }
      width="36rem"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="gold"
            onClick={submit}
            loading={busy}
            disabled={!valid}
          >
            {creating ? t("games.addSubmit") : t("games.saveSettings")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p
          className="rounded-lg px-3 py-2 text-[12px]"
          style={{ background: "var(--info-bg)", color: "var(--info)" }}
        >
          {creating ? t("games.addNote") : t("games.settingsNote")}
        </p>

        <FieldGrid>
          {creating ? (
            <TextField
              label={t("games.slug")}
              value={slug}
              onChange={(value) => setSlug(value.toLowerCase())}
              hint={t("games.slugHint")}
              error={slug.trim() && slugInvalid ? t("games.slugError") : null}
              mono
              required
            />
          ) : (
            /* Read-only on an existing tile: the slug addresses the game in the
               API and keys the player app's artwork, so renaming it breaks both
               quietly. The API does not accept a change to it either. */
            <TextField
              label={t("games.slug")}
              value={game?.slug ?? ""}
              onChange={() => {}}
              hint={t("games.slugLocked")}
              mono
              disabled
            />
          )}

          <TextField label={t("games.name")} value={name} onChange={setName} required />
          <TextField label={t("games.subtitle")} value={subtitle} onChange={setSubtitle} />
          <TextField
            label={t("games.badge")}
            value={badge}
            onChange={setBadge}
            hint={t("games.badgeHint")}
          />
          <TextField
            label={t("games.tileImage")}
            value={tileImage}
            onChange={setTileImage}
            hint={t("games.tileImageHint")}
          />
          <NumberField
            label={t("games.sortOrder")}
            value={sortOrder}
            onChange={setSortOrder}
            hint={t("games.sortOrderHint")}
            error={sortInvalid ? t("games.sortOrderError") : null}
          />

          <SelectField
            label={t("games.interactionLabel")}
            value={interaction}
            onChange={setInteraction}
            hint={t(`games.interactionHint.${interaction}` as TranslationKey)}
            options={GAME_INTERACTIONS.map((value) => ({
              value,
              label: t(`games.interaction.${value}` as TranslationKey),
            }))}
          />

          <div className="flex items-end pb-1">
            <Toggle
              label={t("games.hiddenToggle")}
              hint={isHidden ? t("games.hiddenHint") : t("games.shownHint")}
              checked={isHidden}
              onChange={setIsHidden}
              tone="danger"
            />
          </div>
        </FieldGrid>

        {/* A playable game that is hidden keeps running — the scheduler does not
            read this flag. Worth saying here, because the natural reading of
            "hidden" is "switched off". */}
        {isHidden && opensTheGame ? (
          <p
            className="rounded-lg px-3 py-2 text-[12px]"
            style={{ background: "var(--warning-bg)", color: "var(--warning)" }}
          >
            {t("games.hiddenPlayableWarning")}
          </p>
        ) : null}

        {showRoundSettings ? (
          <>
            <p className="eyebrow">{t("games.roundSettings")}</p>

            <FieldGrid>
              <NumberField
                label={t("games.numbersPerRound")}
                value={positions}
                onChange={setPositions}
                hint={t("games.numbersHint")}
                error={
                  positionsNum < 1 || positionsNum > 20 ? t("games.numbersError") : null
                }
                required
              />

              <NumberField
                label={t("games.interval")}
                value={interval}
                onChange={setIntervalValue}
                hint={
                  intervalNum >= 30
                    ? t("games.intervalHint", { value: duration(intervalNum) })
                    : t("games.intervalMin")
                }
                error={
                  intervalNum < 30 || intervalNum > 86_400
                    ? t("games.intervalError")
                    : null
                }
                required
              />

              <NumberField
                label={t("games.lockdown")}
                value={lockdown}
                onChange={setLockdown}
                hint={
                  lockdownInvalid
                    ? t("games.lockdownMax", { value: Math.max(0, intervalNum - 1) })
                    : lockdownNum > 0
                      ? t("games.lockdownHint", { value: duration(lockdownNum) })
                      : t("games.lockdownOffHint")
                }
                error={lockdownInvalid ? t("games.lockdownError") : null}
                required
              />

              <NumberField
                label={t("games.lowest")}
                value={numberMin}
                onChange={setNumberMin}
                required
              />
              <NumberField
                label={t("games.highest")}
                value={numberMax}
                onChange={setNumberMax}
                error={rangeInvalid ? t("games.rangeError") : null}
                required
              />

              <NumberField
                label={t("games.oddsBp")}
                value={oddsBp}
                onChange={setOddsBp}
                hint={
                  oddsNum >= 10_000
                    ? t("games.oddsHint", { value: odds(oddsNum) })
                    : t("games.oddsMin")
                }
                error={oddsNum < 10_000 ? t("games.oddsError") : null}
                required
              />
            </FieldGrid>
          </>
        ) : null}

        <TextAreaField
          label={t("common.reason")}
          value={reason}
          onChange={setReason}
          placeholder={t("games.reasonPlaceholder")}
          hint={t("common.reasonHint")}
        />

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

/* ── The rounds ahead ──────────────────────────────────────────────────────── */

/**
 * What the next rounds will open on, and the means to change it.
 *
 * Results are drawn some rounds in advance and held in a queue, which is the
 * only reason this screen can exist: a result decided at the moment of drawing
 * is one nobody can be shown beforehand, let alone correct.
 *
 * **The first row is the round counting down now.** Editing it sets the result
 * of the round players are betting on as the operator reads the screen, so it
 * is marked as live rather than left to be inferred from a round number. Every
 * row after it is an estimate — round numbers and times are arithmetic on the
 * interval, and both shift the moment a round is voided or the game is paused,
 * which the column header says in as many words.
 *
 * A live subscription rather than a fetch, because the list moves on its own:
 * the head is consumed every time a round draws and a fresh entry appears at the
 * bottom. A screen that read it once would be offering an operator the chance to
 * edit an entry that has already been played.
 */
function UpcomingRoundsPanel({
  game,
  canUpdate,
}: {
  game: AdminGame;
  canUpdate: boolean;
}) {
  const t = useT();
  const toast = useToast();
  const upcoming = useLive<UpcomingRounds>("upcoming-rounds", { id: game.id });
  const [editing, setEditing] = useState<UpcomingRound | null>(null);
  const [rerolling, setRerolling] = useState<string | null>(null);

  async function reroll(round: UpcomingRound) {
    setRerolling(round.id);
    try {
      await api.regenerateUpcoming(game.id, round.id);
      toast.success(
        t("toast.upcomingRerolled"),
        t("toast.upcomingRerolledDetail", { drawNo: round.drawNo }),
      );
      upcoming.reload();
    } catch (caught) {
      toast.error(
        t("error.upcoming"),
        caught instanceof ApiError ? caught.message : undefined,
      );
    } finally {
      setRerolling(null);
    }
  }

  const rows = upcoming.data?.items ?? [];

  return (
    <Panel
      eyebrow={t("section.rounds")}
      title={t("upcoming.title")}
      actions={
        <div className="flex items-center gap-2">
          {upcoming.data && !upcoming.data.isRunning ? (
            <Badge tone="warning">{t("upcoming.paused")}</Badge>
          ) : null}
          <Button size="sm" onClick={upcoming.reload}>
            {t("action.refresh")}
          </Button>
        </div>
      }
    >
      <p className="text-muted mb-4 text-[13px]">{t("upcoming.explain")}</p>

      {upcoming.error ? (
        <ErrorNote message={upcoming.error} onRetry={upcoming.reload} />
      ) : null}

      <Table
        head={[
          t("upcoming.round"),
          t("upcoming.drawsAt"),
          t("upcoming.numbers"),
          t("upcoming.origin"),
          <span key="a" className="block text-right">
            {t("col.actions")}
          </span>,
        ]}
      >
        {upcoming.loading && rows.length === 0 ? (
          <LoadingRows columns={5} rows={4} />
        ) : rows.length === 0 ? (
          <Tr>
            <Td colSpan={5}>
              <p className="text-dim py-3 text-[13px]">{t("upcoming.empty")}</p>
            </Td>
          </Tr>
        ) : (
          rows.map((round) => (
            <Tr key={round.id}>
              <Td>
                <div className="flex items-center gap-2">
                  <span className="money">#{round.drawNo}</span>
                  {round.isProjected ? null : (
                    <Badge tone="success">{t("upcoming.live")}</Badge>
                  )}
                </div>
                {round.isProjected ? (
                  <p className="text-dim mt-0.5 text-[11px]">
                    {t("upcoming.estimated")}
                  </p>
                ) : null}
              </Td>
              <Td>
                <span className="money">{timeOnly(round.drawsAt)}</span>
                <p className="text-dim mt-0.5 text-[11px]" title={dateTime(round.drawsAt)}>
                  {relative(round.drawsAt)}
                </p>
              </Td>
              <Td>
                <div className="flex flex-wrap gap-1.5">
                  {round.numbers.map((value, index) => (
                    <NumberChip key={index} value={value} size="sm" />
                  ))}
                </div>
              </Td>
              <Td>
                {round.isManual ? (
                  <Badge tone="gold">{t("upcoming.manual")}</Badge>
                ) : (
                  <Badge tone="neutral">{t("upcoming.auto")}</Badge>
                )}
                {/* A hand-set entry the game has since outgrown. It is kept
                    rather than overwritten, but a round reaching it would draw
                    fresh numbers — so it is worth saying before that happens. */}
                {round.fitsCurrentSettings ? null : (
                  <p className="mt-1 text-[11px]" style={{ color: "var(--warning)" }}>
                    {t("upcoming.stale")}
                  </p>
                )}
              </Td>
              <Td>
                {canUpdate ? (
                  <RowActions>
                    <ActionButton
                      icon={ActionIcon.edit}
                      tone="primary"
                      onClick={() => setEditing(round)}
                    >
                      {t("action.edit")}
                    </ActionButton>
                    <ActionButton
                      icon={ActionIcon.reroll}
                      loading={rerolling === round.id}
                      onClick={() => reroll(round)}
                    >
                      {t("upcoming.reroll")}
                    </ActionButton>
                  </RowActions>
                ) : (
                  <NoRowActions reason={t("action.noPermission")} />
                )}
              </Td>
            </Tr>
          ))
        )}
      </Table>

      <UpcomingRoundDialog
        game={game}
        round={editing}
        shape={upcoming.data ?? null}
        onClose={() => setEditing(null)}
        onDone={upcoming.reload}
      />
    </Panel>
  );
}

/**
 * Sets the numbers one upcoming round will open on.
 *
 * The same `NumberSlots` control the Draws screen uses to correct a result, for
 * the same reason: one box per position, typed like a PIN. What differs is the
 * warning — correcting a settled result moves money that has already been paid,
 * while this decides money that has not been staked yet, and the round the
 * player is currently betting on is exactly the case where that distinction
 * stops being comfortable. The dialog says which of the two you are doing.
 */
function UpcomingRoundDialog({
  game,
  round,
  shape,
  onClose,
  onDone,
}: {
  game: AdminGame;
  round: UpcomingRound | null;
  shape: UpcomingRounds | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [values, setValues] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const positionCount = shape?.positionCount ?? round?.numbers.length ?? 0;
  const min = shape?.numberRange.min ?? 0;
  const max = shape?.numberRange.max ?? 0;

  useEffect(() => {
    if (!round) return;
    /*
     * Seeded from the entry's own numbers, padded to the game's current shape.
     *
     * The two differ exactly when an operator's earlier choice was made under a
     * different position count — the row the dialog opened from is flagged for
     * it — and seeding from the entry alone would then render a form that
     * cannot produce a valid save.
     */
    setValues(
      Array.from({ length: positionCount }, (_, index) =>
        round.numbers[index] === undefined ? "" : String(round.numbers[index]),
      ),
    );
    setReason("");
    setError(null);
  }, [round, positionCount]);

  const numbers = values.map((value) => Number(value));
  const complete =
    values.length === positionCount &&
    values.every((value) => value !== "") &&
    numbers.every((value) => Number.isInteger(value) && value >= min && value <= max);

  async function submit() {
    if (!round || !complete) return;
    setBusy(true);
    setError(null);
    try {
      await api.setUpcomingNumbers(game.id, round.id, numbers, reason.trim() || undefined);
      toast.success(
        t("toast.upcomingSaved"),
        t("toast.upcomingSavedDetail", { drawNo: round.drawNo }),
      );
      onDone();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("error.upcoming"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(round)}
      onClose={onClose}
      eyebrow={t("section.rounds")}
      title={t("upcoming.editTitle", { drawNo: round?.drawNo ?? 0 })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button variant="gold" onClick={submit} loading={busy} disabled={!complete}>
            {t("upcoming.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p
          className="rounded-lg px-3 py-2 text-[12px]"
          style={
            round?.isProjected
              ? { background: "var(--info-bg)", color: "var(--info)" }
              : { background: "var(--warning-bg)", color: "var(--warning)" }
          }
        >
          {round?.isProjected
            ? t("upcoming.editProjected")
            : t("upcoming.editLive", {
                period: t("common.roundNo", { drawNo: round?.drawNo ?? "" }),
              })}
        </p>

        <NumberSlots
          values={values}
          onChange={setValues}
          min={min}
          max={max}
          ariaLabel={t("upcoming.numbers")}
        />
        <p className="text-dim text-[12px]">
          {t("upcoming.rangeHint", { count: positionCount, min, max })}
        </p>

        <TextAreaField
          label={t("common.reason")}
          value={reason}
          onChange={setReason}
          placeholder={t("upcoming.reasonPlaceholder")}
          hint={t("common.reasonHint")}
        />

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}
